import { QueryParams } from 'common/urls/QueryParams';
import { first } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { usePersistentDeliveryDates } from 'src/dish-first/usePersistentDeliveryDates';
import {
  BrowseFilter,
  CategoryPageContent,
  DishFirstExploreSegmentShefFragment,
  DishFirstFoodItemsSegmentFragment,
  DishFirstSegmentFilter,
} from 'src/gqlReactTypings.generated.d';
import {
  ExploreShefCard,
  ExploreShefCardOnClickParams,
} from 'src/pages/consumer/dish-first/explore/components/Segments/ExploreShefCard';
import { ShefCuisineSegmentHeader } from 'src/pages/consumer/dish-first/explore/components/Segments/SegmentHeader/ShefCuisineSegmentHeader';
import { ShefSegmentHeader } from 'src/pages/consumer/dish-first/explore/components/Segments/SegmentHeader/ShefSegmentHeader';
import { ISegmentMetrics } from 'src/pages/consumer/explore/SegmentAnalytics';
import { QueryParamsUpdates, QueryParamsUpdateType } from 'src/pages/consumer/explore/useExploreUrl';
import { Carousel } from 'src/shared/design-system/DishFirst/Carousel';
import { ScrollPosition } from 'src/shared/design-system/DynamicImage';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useTracker } from 'src/shared/hooks/useTracker';
import { match, P } from 'ts-pattern';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { useFilterQueryParams } from '../../../../../../shared/design-system/DishFirst/DishModal/utils';
import { trackDishCardClicked } from '../../../../../../shared/design-system/DishFirstCard';
import { getExploreShefMenuUrl } from '../../utils';
import { trackSeeAllClick } from './DishFirstCardRow';
import { BackgroundContainer, MaxWidthContainer, RowContainer, ShefSeeAllCard } from './styles';
import { getQueryParamsFromSeeAllFilter, isExploreSegmentShef } from './utils';

const isSegmentFilter = (id): id is DishFirstSegmentFilter => Object.values(DishFirstSegmentFilter).includes(id);
export const isCategoryPageContent = (content): content is CategoryPageContent =>
  content?.__typename === 'CategoryPageContent';

interface IProps {
  className?: string;
  segment: DishFirstFoodItemsSegmentFragment;
  segmentMetrics: ISegmentMetrics;
  updateQueryParams: (updates: QueryParamsUpdates<DishFirstSegmentFilter>, updateType?: QueryParamsUpdateType) => void;
  scrollPosition?: ScrollPosition;
  seeAllUrl?: string;
  showSeeAllButton?: boolean;
  showHeaderAndSubtitle?: boolean;
  showScrollButtons?: boolean;
}

export const DishFirstShefsCardRow: React.FC<IProps> = ({
  className,
  segment,
  segmentMetrics,
  scrollPosition,
  updateQueryParams,
  seeAllUrl,
  showSeeAllButton,
  showHeaderAndSubtitle = true,
  showScrollButtons = true,
}): JSX.Element => {
  const { isSmallScreen } = useScreenContext();
  const [deliveryDates] = usePersistentDeliveryDates();
  const deliveryDate = first(deliveryDates);

  const history = useHistory();

  const items: DishFirstExploreSegmentShefFragment[] = useMemo(
    () => segment.content.items.filter(isExploreSegmentShef),
    [segment.content.items]
  );
  const tracker = useTracker();
  const { appendFiltersQueryStringToUrl, filtersQueryParams } = useFilterQueryParams();

  const openShefMenu = useCallback(
    ({ exploreSegmentShef, idx, segmentMetrics: metrics }: ExploreShefCardOnClickParams) => {
      trackDishCardClicked(tracker, segmentMetrics, 'shef', filtersQueryParams, idx ?? -1);
      const shefPage = appendFiltersQueryStringToUrl(
        getExploreShefMenuUrl(exploreSegmentShef.shef.id, deliveryDate, metrics, idx)
      );
      history.push(shefPage);
    },
    [history, deliveryDate, filtersQueryParams, segmentMetrics, tracker, appendFiltersQueryStringToUrl]
  );

  const handleSeeAllButtonClick = useCallback(() => {
    trackSeeAllClick(tracker, segmentMetrics, 'shef', filtersQueryParams);
    if (seeAllUrl) {
      history.push(seeAllUrl);
    }

    if (segment.preview?.seeAllFilter) {
      const updatedQueryParams = getQueryParamsFromSeeAllFilter(segment.preview.seeAllFilter);
      if (Object.keys(updatedQueryParams).length === 1 && !!updatedQueryParams.cuisine) {
        updateQueryParams({ ...updatedQueryParams, [QueryParams.BROWSE_FILTER]: BrowseFilter.Cuisine });
      } else {
        updateQueryParams(updatedQueryParams);
      }
    } else if (isSegmentFilter(segment.id)) {
      updateQueryParams({ [QueryParams.SEGMENT_FILTER]: [segment.id] });
    }
  }, [
    tracker,
    segment.id,
    segment?.preview?.seeAllFilter,
    updateQueryParams,
    seeAllUrl,
    history,
    filtersQueryParams,
    segmentMetrics,
  ]);

  const showSeeAllButtonFinal =
    showSeeAllButton !== undefined ? showSeeAllButton : segment.content.total > segment.content.items.length;
  // This works - but we need to ensure that we're getting our subcuisine segments on cuisine category pages
  const categoryPageContent = isCategoryPageContent(segment.categoryPageContent) ? segment.categoryPageContent : null;
  const header = match({ showHeaderAndSubtitle, categoryPageContent })
    .with({ showHeaderAndSubtitle: true, categoryPageContent: P.not(P.nullish) }, ({ categoryPageContent }) => (
      <ShefCuisineSegmentHeader
        segment={segment}
        categoryPageContent={categoryPageContent}
        showSeeAllButton={showSeeAllButtonFinal}
        handleSeeAllButtonClick={handleSeeAllButtonClick}
      />
    ))
    .with({ showHeaderAndSubtitle: true, categoryPageContent: P.nullish }, () => (
      <ShefSegmentHeader
        segment={segment}
        showSeeAllButton={showSeeAllButtonFinal}
        handleSeeAllButtonClick={handleSeeAllButtonClick}
      />
    ))
    .otherwise(() => null);

  return (
    <BackgroundContainer
      className={className}
      url={categoryPageContent?.segmentBackgroundUrl ?? ''}
      tabletUrl={categoryPageContent?.segmentBackgroundUrlTablet ?? ''}
      desktopUrl={categoryPageContent?.segmentBackgroundUrlDesktop ?? ''}>
      <MaxWidthContainer>
        {header}
        <RowContainer>
          <Carousel
            content={items}
            scrollButtonsElementName={segment.id}
            showMobileDivider
            showScrollButtons={showScrollButtons}>
            <>
              {items?.map((item, idx) => (
                <ExploreShefCard
                  key={`${segment.id}-${item.shef.id}-${idx}`}
                  deliveryDate={deliveryDate}
                  idx={idx}
                  exploreSegmentShef={item}
                  onClick={openShefMenu}
                  href={appendFiltersQueryStringToUrl(
                    getExploreShefMenuUrl(item.shef.id, deliveryDate, segmentMetrics, idx)
                  )}
                  segment={segment}
                  segmentMetrics={segmentMetrics}
                  location='dish-first-explore-page'
                  isResponsive={false}
                  scrollPosition={scrollPosition}
                />
              ))}
              {showSeeAllButtonFinal && isSmallScreen && <ShefSeeAllCard onClick={handleSeeAllButtonClick} />}
            </>
          </Carousel>
        </RowContainer>
      </MaxWidthContainer>
    </BackgroundContainer>
  );
};
